import { normalizeTierId, TIER_IDS, type TierId } from '../config/PricingConfig'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import type { Workspace, WorkspaceRole } from '../types/workspace'
import { roleCanPerform } from '../utils/workspacePermissions'
import { isWorkspaceOwner } from '../utils/workspaceOwners'
import { isModuleEnabled } from '../utils/workspaceModules'

const TIER_RANK = Object.fromEntries(TIER_IDS.map((tier, index) => [tier, index])) as Record<TierId, number>

const STOCK_MIN_TIER: TierId = 'ESSENTIAL_TIER'
const BUDGET_MIN_TIER: TierId = 'GROWTH_TIER'
const QUOTA_MIN_TIER: TierId = 'GROWTH_TIER'
const TIMESHEETS_MIN_TIER: TierId = 'ESSENTIAL_TIER'
const TIMESHEETS_MONEY_MIN_TIER: TierId = 'PROFESSIONAL_TIER'

// Demo unlock (R35 PD2/PD13): the optional `workspace` lets a demo-unlocked workspace lift the
// TIER requirement only. The module switch and every role check stay in force (see isXEnabled/canX).
export const isStockTierEligible = (plan: unknown, workspace?: unknown) =>
  limitsLiftedForWorkspace(workspace) || TIER_RANK[normalizeTierId(plan)] >= TIER_RANK[STOCK_MIN_TIER]

export const isStockEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  if (!isStockTierEligible(workspace.plan, workspace)) return false
  return isModuleEnabled(workspace, 'stock')
}

export const canViewStock = (workspace: Workspace | null | undefined, role: WorkspaceRole | null | undefined): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isStockEnabled(workspace)) return false
  if (!role) return false
  if (workspace?.isPreview && role === 'READONLY') return true
  return role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR' || role === 'HELPER'
}

export const canMutateStock = (workspace: Workspace | null | undefined, role: WorkspaceRole | null | undefined): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isStockEnabled(workspace)) return false
  return Boolean(role && roleCanPerform(role, 'EDITOR'))
}

// ---------------------------------------------------------------------------
// Budget module
// ---------------------------------------------------------------------------

export const isBudgetTierEligible = (plan: unknown, workspace?: unknown) =>
  limitsLiftedForWorkspace(workspace) || TIER_RANK[normalizeTierId(plan)] >= TIER_RANK[BUDGET_MIN_TIER]

/** Default for new workspaces is OFF. Module must be opt-in. */
export const isBudgetEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  if (!isBudgetTierEligible(workspace.plan, workspace)) return false
  return isModuleEnabled(workspace, 'budget')
}

export const canViewBudget = (workspace: Workspace | null | undefined, role: WorkspaceRole | null | undefined): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isBudgetEnabled(workspace)) return false
  if (!role) return false
  if (workspace?.isPreview && role === 'READONLY') return true
  // VIEWER and HELPER may see budget summaries; mutation is gated separately.
  return role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR' || role === 'HELPER' || role === 'VIEWER'
}

/** Any member with write access to the linked entity may log an expense or
 * a reversal (ratified: "Everyday Logging: Any member with write access"). */
export const canMutateBudget = (workspace: Workspace | null | undefined, role: WorkspaceRole | null | undefined): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isBudgetEnabled(workspace)) return false
  return Boolean(role && roleCanPerform(role, 'EDITOR'))
}

/** OWNER-only: force-complete an over-budget Task/Goal. Determined by
 * comparing the actor uid to `workspace.ownerId`; the role enum has no
 * OWNER value distinct from ADMIN. */
export const canOverrideBudget = (
  workspace: Workspace | null | undefined,
  actorId: string | null | undefined,
): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace && actorId)
  if (!isBudgetEnabled(workspace) || !workspace || !actorId) return false
  return isWorkspaceOwner(workspace, actorId)
}

/** OWNER-only: change workspace currency. Blocked at the service level if
 * any expenses already exist. */
export const canChangeCurrency = (
  workspace: Workspace | null | undefined,
  actorId: string | null | undefined,
): boolean => {
  if (!workspace || !actorId) return false
  return isWorkspaceOwner(workspace, actorId)
}

export const getBudgetEnforcementMode = (workspace: Workspace | null | undefined): 'warn' | 'block' =>
  workspace?.modules?.budgetEnforcement === 'block' ? 'block' : 'warn'

// ---------------------------------------------------------------------------
// Quota module
// ---------------------------------------------------------------------------

export const isQuotaTierEligible = (plan: unknown, workspace?: unknown) =>
  limitsLiftedForWorkspace(workspace) || TIER_RANK[normalizeTierId(plan)] >= TIER_RANK[QUOTA_MIN_TIER]

/** Default for new workspaces is OFF. Module must be opt-in. */
export const isQuotaEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  if (!isQuotaTierEligible(workspace.plan, workspace)) return false
  return isModuleEnabled(workspace, 'quota')
}

/** All workspace roles may view leaderboards + history; Management is gated
 *  separately via canManageQuota. */
export const canViewQuota = (workspace: Workspace | null | undefined, role: WorkspaceRole | null | undefined): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isQuotaEnabled(workspace)) return false
  if (!role) return false
  return role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR' || role === 'HELPER' || role === 'VIEWER' || role === 'READONLY'
}

/** Create / edit / soft-delete quotas — ADMIN only per ratified spec. */
export const canManageQuota = (workspace: Workspace | null | undefined, role: WorkspaceRole | null | undefined): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isQuotaEnabled(workspace)) return false
  return Boolean(role && roleCanPerform(role, 'ADMIN'))
}

/** Teaching module (ED10: all plans, Free included; no tier check). */
export const isTeachingEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'teaching')
}

/** Training module (ED10: all plans, Free included; no tier check). */
export const isTrainingEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'training')
}

/** Construction operations module (R39: no tier check before D1). */
export const isConstructionEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'construction')
}

/** Hospital operations module (R39: no tier check before D1). */
export const isHospitalEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'hospital')
}

/** Sites operations module (R39 G1: no tier check before D1). */
export const isSitesEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'sites')
}

/** Delivery operations module (R39 E1). */
export const isDeliveryEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'delivery')
}

/** Shops operations module (R39 F1). */
export const isShopsEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'shops')
}

/** Stays operations module (R39 K1). */
export const isStaysEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'stays')
}

/** Jobs operations module (R39 H1). */
export const isJobsEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'jobs')
}

/** Clinics operations module (R39 M1). */
export const isClinicsEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'clinics')
}

/** Plant operations module (R39 J1). */
export const isPlantEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  return isModuleEnabled(workspace, 'plant')
}

/** Operations core enabled helper: true when any sector module is on. */
export const opsCoreEnabled = (workspace: Workspace | null | undefined): boolean => {
  return (
    isConstructionEnabled(workspace) ||
    isHospitalEnabled(workspace) ||
    isSitesEnabled(workspace) ||
    isDeliveryEnabled(workspace) ||
    isShopsEnabled(workspace) ||
    isStaysEnabled(workspace) ||
    isJobsEnabled(workspace) ||
    isClinicsEnabled(workspace) ||
    isPlantEnabled(workspace)
  )
}

// ---------------------------------------------------------------------------
// Client outreach module
// ---------------------------------------------------------------------------

export const isClientOutreachTierEligible = (
  plan: unknown,
  workspace?: unknown,
  hasActiveLifetimePass = false,
): boolean =>
  limitsLiftedForWorkspace(workspace) || hasActiveLifetimePass || normalizeTierId(plan) !== 'FREE_TIER'

export const isClientOutreachEnabled = (
  workspace: Workspace | null | undefined,
  hasActiveLifetimePass = false,
): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  if (!isClientOutreachTierEligible(workspace.plan, workspace, hasActiveLifetimePass)) return false
  return isModuleEnabled(workspace, 'clientOutreach')
}

export const canViewClients = (
  workspace: Workspace | null | undefined,
  role: WorkspaceRole | null | undefined,
  hasActiveLifetimePass = false,
): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isClientOutreachEnabled(workspace, hasActiveLifetimePass)) return false
  if (!role) return false
  return role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR'
}

export const canManageClients = (
  workspace: Workspace | null | undefined,
  role: WorkspaceRole | null | undefined,
  hasActiveLifetimePass = false,
): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!isClientOutreachEnabled(workspace, hasActiveLifetimePass)) return false
  if (!role) return false
  return role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR'
}

export const canEraseClient = (
  workspace: Workspace | null | undefined,
  role: WorkspaceRole | null | undefined,
): boolean => {
  if (testingOverrideAllowsAllFeatures()) return Boolean(workspace)
  if (!workspace || !role) return false
  return role === 'OWNER' || role === 'ADMIN'
}

// ---------------------------------------------------------------------------
// Timesheets module (R19 TS0.b / TS2.b)
// ---------------------------------------------------------------------------

export const isTimesheetsTierEligible = (plan: unknown, workspace?: unknown): boolean =>
  limitsLiftedForWorkspace(workspace) || TIER_RANK[normalizeTierId(plan)] >= TIER_RANK[TIMESHEETS_MIN_TIER]

export const isTimesheetsMoneyTierEligible = (plan: unknown, workspace?: unknown): boolean =>
  limitsLiftedForWorkspace(workspace) || TIER_RANK[normalizeTierId(plan)] >= TIER_RANK[TIMESHEETS_MONEY_MIN_TIER]

/** Default for new workspaces is OFF. Module must be opt-in. */
export const isTimesheetsEnabled = (workspace: Workspace | null | undefined): boolean => {
  if (!workspace) return false
  if (testingOverrideAllowsAllFeatures()) return true
  if (!isTimesheetsTierEligible(workspace.plan, workspace)) return false
  return isModuleEnabled(workspace, 'timesheets')
}

export const featureRegistry = {
  isStockTierEligible,
  isStockEnabled,
  canViewStock,
  canMutateStock,
  isBudgetTierEligible,
  isBudgetEnabled,
  canViewBudget,
  canMutateBudget,
  canOverrideBudget,
  canChangeCurrency,
  getBudgetEnforcementMode,
  isQuotaTierEligible,
  isQuotaEnabled,
  canViewQuota,
  canManageQuota,
  isTeachingEnabled,
  isTrainingEnabled,
  isConstructionEnabled,
  isHospitalEnabled,
  opsCoreEnabled,
  isClientOutreachTierEligible,
  isClientOutreachEnabled,
  canViewClients,
  canManageClients,
  canEraseClient,
  isTimesheetsTierEligible,
  isTimesheetsMoneyTierEligible,
  isTimesheetsEnabled,
}

